import { initializeApp } from 'firebase/app'
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check'
import type { AppCheck } from 'firebase/app-check'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { initializeFirestore } from 'firebase/firestore'
import { getStorage } from 'firebase/storage'
import { getFunctions } from 'firebase/functions'

import { decideAppCheck } from './appCheck'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

// ¿Este arranque habla con los emuladores? `import.meta.env.DEV` es verdadero sólo
// bajo el servidor de desarrollo (`vite --mode dev`) y falso en TODO `vite build`,
// incluido `--mode dev`. Es UNA sola constante a propósito: gobierna a la vez la
// conexión a los emuladores (al final de este archivo) y la decisión de App Check,
// para que las dos no puedan divergir (ADR-SEC-014).
const usesEmulators = import.meta.env.DEV

export const firebaseApp = initializeApp(firebaseConfig)

// App Check (ADR-SEC-014 local): el portal atesta con reCAPTCHA Enterprise ANTES de
// que exista cualquier servicio, para que ninguna petición a Auth, Firestore, Storage
// o a la callable pueda salir sin token. El orden lo fija
// tests/unit/services/firebase/config.appCheck.test.ts. Con los emuladores no se
// inicializa (no exigen App Check y `localhost` no está en ninguna llave); fuera de
// ellos, sin clave, `decideAppCheck` LANZA y la app no monta — nunca se inicializa
// con una clave vacía en silencio. App Check no autoriza nada: las reglas siguen
// siendo la frontera (ADR-SEC-003/004); el enforcement por servicio lo enciende el
// ecosistema cuando el tráfico sea «Verified».
const appCheckDecision = decideAppCheck({
  usesEmulators,
  siteKey: import.meta.env.VITE_APPCHECK_RECAPTCHA_SITE_KEY,
})
export const appCheck: AppCheck | null =
  appCheckDecision.kind === 'init'
    ? initializeAppCheck(firebaseApp, {
        provider: new ReCaptchaEnterpriseProvider(appCheckDecision.siteKey),
        // No es el default del SDK: sin esto el token (TTL 1 h) no se renueva en
        // segundo plano y una sesión abierta se quedaría sin atestación a la hora.
        isTokenAutoRefreshEnabled: true,
      })
    : null

export const auth = getAuth(firebaseApp)
// Firestore se inicializa forzando long polling (ADR-003 no-SEC, supersede
// ADR-002): en navegadores WebKit (Safari y todo navegador en iOS/iPadOS) el
// transporte WebChannel por defecto se cuelga de forma intermitente detrás de
// ciertas redes/proxies. La auto-detección (ADR-002) reaccionaba tarde y el
// fallo reapareció en campo (iPad/dev, 2026-06-21), así que se fuerza long
// polling (HTTP) desde el arranque: nunca se intenta WebChannel. El portal es de
// uso mayoritariamente móvil (iPhone/iPad), donde la fiabilidad pesa más que el
// overhead HTTP en escritorio.
export const db = initializeFirestore(firebaseApp, {
  experimentalForceLongPolling: true,
})
export const storage = getStorage(firebaseApp)
// Cloud Functions: el portal invoca UNA sola callable, `acceptLegalTerms`
// (productor: quizoteca-admin, desplegada en us-central1 — ADR-SEC-013 local,
// specs/API-CONTRACT/accept-legal-terms.md). La región es la del productor; si
// admin la mueve, este literal se mueve con ella (specs/COHERENCIA.md §2).
// Sin emulador local: este repo no hospeda funciones.
export const functions = getFunctions(firebaseApp, 'us-central1')

/**
 * Promise that resolves once Firebase Auth has restored the session from IndexedDB.
 * Router guards must await this before checking auth state on initial load.
 */
export const authReady: Promise<void> = new Promise((resolve) => {
  const unsubscribe = onAuthStateChanged(auth, () => {
    unsubscribe()
    resolve()
  })
})

// Conectar a los emuladores en desarrollo. La condición es la misma constante que
// decidió no inicializar App Check (arriba).
if (usesEmulators) {
  import('firebase/firestore').then(({ connectFirestoreEmulator }) => {
    try {
      connectFirestoreEmulator(db, 'localhost', 8080)
      console.log('Firestore Emulator conectado en localhost:8080')
    } catch (_error) {
      console.debug('Firestore Emulator ya conectado')
    }
  })

  import('firebase/auth').then(({ connectAuthEmulator }) => {
    try {
      connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true })
      console.log('Auth Emulator conectado en localhost:9099')
    } catch (_error) {
      console.debug('Auth Emulator ya conectado')
    }
  })

  import('firebase/storage').then(({ connectStorageEmulator }) => {
    try {
      connectStorageEmulator(storage, 'localhost', 9199)
      console.log('Storage Emulator conectado en localhost:9199')
    } catch (_error) {
      console.debug('Storage Emulator ya conectado')
    }
  })
}
